import { FC } from 'react';
import { Box, Stack, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { localeFormatterHelper } from '../../../../../../shared/helpers/formatter/localeFormatter.helper.ts';
import {
  comparisonChipColors,
  COMPARISON_METRIC_ORDER,
  formatComparisonValue,
  TComparisonMetricValues,
} from './comparison.types.ts';

interface IComparisonCell {
  value?: TComparisonMetricValues;
  date: string;
  row: {
    articleId?: string | null;
    articleNo?: string | null;
    description?: string | null;
    virtualPositionId?: string | null;
  };
}

export const ComparisonCell: FC<IComparisonCell> = ({ value, date, row }) => {
  const { t } = useTranslation();
  if (!row?.articleId || row?.virtualPositionId) return null;

  const values: TComparisonMetricValues = value ?? { ordered: null, sold: null, returned: null };
  const metrics = COMPARISON_METRIC_ORDER.map((key) => ({
    key,
    color: comparisonChipColors[key],
    label: t(`order.comparison_${key}`),
    value: values[key],
  }));

  const tooltip = (
    <Box sx={{ p: 1, minWidth: 180 }}>
      <Typography sx={{ fontWeight: 700 }}>
        {[row.articleNo, row.description].filter(Boolean).join(' ')}
      </Typography>
      <Typography variant='body2' sx={{ color: grey[500], mb: 0.5 }}>
        {localeFormatterHelper.formatDate(date)}
      </Typography>
      {metrics.map((m) => (
        <Stack
          key={m.key}
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          spacing={3}
          sx={{ py: 0.25 }}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <SDot sx={{ backgroundColor: m.color }} />
            <Typography variant='body2'>{m.label}</Typography>
          </Stack>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {formatComparisonValue(m.value)}
          </Typography>
        </Stack>
      ))}
    </Box>
  );

  return (
    <SComparisonTooltip title={tooltip} placement='top' arrow>
      <Stack
        direction='row'
        spacing={0.5}
        sx={{ width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}
      >
        {metrics.map((m) => (
          <SChip key={m.key} sx={{ backgroundColor: m.color }}>
            {formatComparisonValue(m.value)}
          </SChip>
        ))}
      </Stack>
    </SComparisonTooltip>
  );
};

const SChip = styled(Box)(() => ({
  minWidth: 24,
  height: 18,
  padding: '0 6px',
  borderRadius: 9,
  color: '#fff',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: '18px',
  textAlign: 'center',
}));

const SDot = styled(Box)(({ theme }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  flexShrink: 0,
  border: `1px solid ${theme.palette.divider}`,
}));

const SComparisonTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[3],
    border: `1px solid ${theme.palette.divider}`,
    padding: 0,
    maxWidth: 320,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.paper,
  },
}));
